import { useEffect, useRef, useState } from "react";
import { useInViewOnce } from "@/hooks/use-in-view-once";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

export function StatCounter({
  value,
  suffix,
  startOnMount = false,
}: {
  value: number;
  suffix?: string;
  /** Start counting as soon as mounted (useful for above-the-fold stats). */
  startOnMount?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInViewOnce(ref, "0px");
  const reduced = usePrefersReducedMotion();
  const shouldRun = startOnMount || inView;
  const [display, setDisplay] = useState(reduced ? value : 0);

  useEffect(() => {
    if (!shouldRun) return;
    if (reduced) {
      setDisplay(value);
      return;
    }
    const duration = 1400;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [shouldRun, reduced, value]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}
